// Who completed the property strategy tool (Stage 2, docs/property-strategy-spec.md S2.9), at
// /admin/webinars/strategy-tool, beside the replay registrations: Name, Email, Completed, Source, Strategy and Sent,
// newest first, with Download CSV in the house way (admin/ui/csv.js). It reads hub.strategy_results under its own
// policy, which answers owner and admin at 2-step login and nothing for anyone else (support and hosts see the refusal
// line, and View as reads only the viewed member's own rows). A table on a laptop; on a phone each row stacks into a
// card (admin/ui/Table.jsx). Test people are marked. Undrawn: no board has it.
import { useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { Button, Crumbs, EmptyState, Loading, Problem, Table, downloadCsv, ukDateTime, adminPath, useLoad } from '../ui/index.js'
import { COPY } from './copy.js'
import { leadWords, STRATEGY_CSV } from './model.js'
import { loadStrategyResults, STRATEGY_LIMIT } from './data.js'

const OA = role => role === 'owner' || role === 'admin'

export function StrategyCompletions({ api, role }) {
  const oa = OA(role)
  const list = useLoad(() => (oa ? loadStrategyResults(api) : Promise.resolve([])), [api, oa])
  const [downloaded, setDownloaded] = useState(false)

  const crumbs = <Crumbs items={[{ label: COPY.tabs.webinars, to: adminPath.webinars() }, { label: COPY.stCrumb }]} />
  if (!oa) return <>{crumbs}<div className="ad-card"><EmptyState>{COPY.stOnlyOwnerAdmin}</EmptyState></div></>

  const rows = list.data || []
  function csv() {
    downloadCsv(COPY.stCsvName, STRATEGY_CSV, rows)
    setDownloaded(true)
  }

  const columns = [
    {
      key: 'name', label: COPY.stHeads.name, width: 'minmax(0, 0.9fr)', primary: true,
      render: r => (
        <span className="adc-rr-name">
          {r.person_id
            ? <Link to={adminPath.person(r.person_id)} className="ad-td-main">{r.first_name || COPY.stNoName}</Link>
            : <span className="ad-td-main">{r.first_name || COPY.stNoName}</span>}
          {r.person?.is_test && <span className="ad-tag">{COPY.stTest}</span>}
          {r.made_account && <span className="ad-td-sub">{COPY.stNewAccount}</span>}
        </span>
      ),
    },
    { key: 'email', label: COPY.stHeads.email, width: 'minmax(0, 1.2fr)', render: r => <span className="adc-break">{r.email}</span> },
    { key: 'completed', label: COPY.stHeads.completed, width: 'minmax(0, 0.9fr)', render: r => <span>{ukDateTime(r.created_at)}</span> },
    { key: 'source', label: COPY.stHeads.source, width: 'minmax(0, 0.7fr)', render: r => <span className={r.source ? 'adc-break' : 'ad-td-quiet'}>{r.source || COPY.stNoSource}</span> },
    { key: 'strategy', label: COPY.stHeads.strategy, width: 'minmax(0, 1.1fr)', render: r => <span>{r.strategy_name}</span> },
    {
      key: 'sent', label: COPY.stHeads.sent, width: 'minmax(0, 0.8fr)',
      render: r => <span className={r.lead_status === 'not_sent' ? 'ad-tag' : 'ad-td-quiet'}>{leadWords(r.lead_status)}</span>,
    },
  ]

  return (
    <>
      {crumbs}
      <section className="ad-card adc-rr-list" aria-labelledby="adc-st-list-h">
        <div className="ad-card-head adc-rr-list-head">
          <span className="adc-rr-list-text">
            <h2 className="ad-card-title" id="adc-st-list-h">{COPY.stTitle}</h2>
          </span>
          {rows.length > 0 && <Button size="sm" onClick={csv}>{downloaded ? COPY.stCsvDone : COPY.stCsv}</Button>}
        </div>
        {list.status === 'loading' && !list.data && <Loading />}
        {list.status === 'error' && !list.data && <Problem error={list.error} onRetry={list.reload} />}
        {list.data && (
          <>
            {rows.length > 0 && <p className="adc-rr-count">{COPY.stCount(rows.length)}</p>}
            {rows.length >= STRATEGY_LIMIT && <p className="adc-rr-count">{COPY.stFull}</p>}
            <Table label={COPY.stTitle} rowKey={r => r.id} rows={rows} columns={columns} empty={COPY.stNone} flush />
          </>
        )}
      </section>
    </>
  )
}
